/**
 * 司机人脸库页面
 */
import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Input, Button, Select, Space, Image, message } from 'antd';
import { useTable } from '@/utils/useTable';
import CommonTable from '@/components/commonTable';
import RenderEllipsisColumn from '@/components/renderEllipsisColumn';
import { getDriverFaceInfo, getDriverArchiveList } from '@/service/driverArchive';
import { motorcadeCarList } from '@/service/fleetRisk';
import { companyListRequest } from '@/service/companyManage';
import DriverDetailModal from '@/components/driverDetailModal';
import styles from '@/styles/pageContent.module.scss';
import { formSearchGutter } from '@/constants/common';

const { Option } = Select;

/**
 * 司机人脸库页面
 */
const DriverFaceLibraryPage: React.FC = () => {
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<any>({});
  const [fleetOptions, setFleetOptions] = useState<any[]>([]);
  const [carOptions, setCarOptions] = useState<any[]>([]); // 车辆选项
  const [selectedFleet, setSelectedFleet] = useState<any>(null); // 选中的车队
  const [modalVisible, setModalVisible] = useState(false); // 详情弹窗可见性
  const [selectedRecord, setSelectedRecord] = useState<any>(null); // 当前选中的司机档案
  const [modalLoading, setModalLoading] = useState(false); // 弹窗加载状态
  const [fleetSearchValue, setFleetSearchValue] = useState(''); // 车队搜索关键词

  // 表格数据hook - 使用真实接口
  const { page, limit, loading, data, totalCount, onPageChange, getData } = useTable({
    requestFunc: getDriverFaceInfo,
    searchParams,
    initPageSize: 40,
  });

  // 获取车队列表
  useEffect(() => {
    fetchFleetList();
  }, []);

  const fetchFleetList = async (name: string = '') => {
    try {
      const res = await companyListRequest({
        page: 1,
        limit: 1000,
        name,
      });
      const fleetData = res.data?.data || [];
      const options = fleetData.map((item: any) => ({
        label: item.name,
        value: item.id, // 使用车队ID作为value，用于查询车辆列表
        id: item.id, // 保留id用于其他用途
      }));
      setFleetOptions(options);
    } catch (error) {
      console.error('获取车队列表失败:', error);
    }
  };

  // 获取车辆列表
  const fetchCarList = async (fleetId: number) => {
    const fleet = fleetOptions.find(f => f.value === fleetId);
    if (!fleet) {
      setCarOptions([]);
      return;
    }
    const fleetName = fleet.label;

    try {
      const res = await motorcadeCarList({ cateName: fleetName });
      const carData = res.data?.data || [];
      const options = carData.map((item: any) => ({
        label: item.plate_number, // 车牌号作为显示
        value: item.id, // carId作为实际值
      }));
      setCarOptions(options);
    } catch (error) {
      console.error('获取车辆列表失败:', error);
      setCarOptions([]);
    }
  };

  // 车队选择变化
  const handleFleetChange = (value: any) => {
    setSelectedFleet(value);
    // 切换车队时清空车牌号
    form.setFieldsValue({ carId: undefined });
    setCarOptions([]);
    
    // 如果选择了车队，获取该车队的车辆列表
    if (value) {
      fetchCarList(value);
    }
  };

  // 搜索
  const onSearch = (values: any) => {
    setSearchParams(values);
  };

  // 查看详情
  const handleViewDetail = async (record: any) => {
    console.log('查看详情:', record);
    
    // 先通过driverName查询所有司机档案
    setModalLoading(true);
    try {
      const response = await getDriverArchiveList({ 
        name: record.driverName,
        page: 1,
        limit: 100 // 设置一个足够大的值以获取所有匹配项
      });
      
      const archiveList = response.data?.data || [];
      
      // 在查询结果中通过driverId匹配对应的档案信息
      const matchedArchive = archiveList.find((item: any) => item.driverId === record.driverId);
      
      if (matchedArchive) {
        setSelectedRecord(matchedArchive);
        setModalVisible(true);
      } else {
        message.warning('未找到对应的司机档案信息');
      }
    } catch (error) {
      console.error('查询司机档案失败:', error);
      message.error('查询司机档案失败，请重试');
    } finally {
      setModalLoading(false);
    }
  };

  // 弹窗关闭
  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedRecord(null);
  };

  // 弹窗确认（只读模式，不需要提交）
  const handleModalOk = () => {
    setModalVisible(false);
    setSelectedRecord(null);
  };

  // 渲染人脸照片列表
  const renderFacePhotos = (urls: string[] | string) => {
    // 处理可能是字符串或数组的情况
    const photoUrls = Array.isArray(urls) ? urls : urls ? [urls] : [];
    
    if (!photoUrls || photoUrls.length === 0) {
      return <span style={{ color: '#999' }}>暂无图片</span>;
    }
    
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Image.PreviewGroup>
          {photoUrls.map((url, index) => (
            <Image key={index} src={url} alt={`人脸照片${index + 1}`} width={60} height={60} />
          ))}
        </Image.PreviewGroup>
      </div>
    );
  };

  // 渲染是否类型
  const renderYesNo = (value: any) => {
    if (value === 1 || value === true || value === '是') return '是';
    if (value === 0 || value === false || value === '否') return '否';
    return '-';
  };

  // 格式化时间
  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return '-';
    return dateTime.replace('T', ' ');
  };

  const columns: any[] = [
    {
      title: '人脸照片',
      dataIndex: 'faceUrl',
      key: 'faceUrl',
      width: 150,
      fixed: 'left' as 'left',
      render: (text: any) => renderFacePhotos(text),
    },
    {
      title: '最新工作车辆所有人',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 180,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '最新工作车辆',
      dataIndex: 'plateNumber',
      key: 'plateNumber',
      width: 150,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '是否关联司机档案',
      dataIndex: 'driverId',
      key: 'driverId',
      width: 150,
      render: (text: number|null) => (text&&text>=0) ? '是' : '否',
    },
    {
      title: '司机姓名',
      dataIndex: 'driverName',
      key: 'driverName',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '人脸信息创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (text: any) => formatDateTime(text),
    },
    {
      title: '人脸信息更新时间',
      dataIndex: 'createTime',
      key: 'updateTime',
      width: 180,
      render: (text: any) => formatDateTime(text),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as 'right',
      render: (text: any, record: any) => (
        <Space size="small">
          {record.driverName && (
            <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
              查看详情
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className={styles.pageContent}>
      <Form form={form} onFinish={onSearch}>
        <Row gutter={formSearchGutter}>
          <Col span={6}>
            <Form.Item name="companyId" label="车队">
              <Select 
                placeholder="请选择" 
                allowClear
                showSearch
                searchValue={fleetSearchValue}
                onSearch={(value) => {
                  setFleetSearchValue(value);
                  fetchFleetList(value);
                }}
                onChange={(value) => {
                  setFleetSearchValue('');
                  handleFleetChange(value);
                }}
                filterOption={false}
              >
                {fleetOptions.map((fleet) => (
                  <Option key={fleet.value} value={fleet.value}>
                    {fleet.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="carId" label="车牌号" >
              <Select
                placeholder={selectedFleet ? "请选择车牌号" : "请先选择车队"} 
                allowClear 
                disabled={!selectedFleet}
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {carOptions.map((car) => (
                  <Option key={car.value} value={car.value}>
                    {car.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="name" label="司机姓名">
              <Input placeholder="请输入司机姓名" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="archiveFlag" label="是否关联司机档案">
              <Select placeholder="请选择" allowClear>
                <Option value={1}>是</Option>
                <Option value={0}>否</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={formSearchGutter}>
          <Col span={24}>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey="id"
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize: limit,
          total: totalCount,
          onChange: onPageChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
      />
      <DriverDetailModal
        visible={modalVisible}
        mode="view"
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        initialValues={selectedRecord}
        confirmLoading={modalLoading}
      />
    </Card>
  );
};

export default DriverFaceLibraryPage;
